import { useId, type ReactNode } from "react";
import { useLocalization, usePreferences, usePreview, type Preview } from "@voltie/localization-web";

import { languagePreferences, languagePreview, localization, type Translate } from "@/app/localization";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";

/** Select value of "no stored language"; never a valid language tag (2-3 letters). */
const AUTOMATIC = "auto";

const PREVIEW_ERROR_KEYS = {
  failed: "common.language.preview_failed",
  unavailable: "common.language.preview_unavailable",
  ended: "common.language.preview_ended",
} as const;

/** A language's own name in the UI language via Intl.DisplayNames, or null when the platform has no name for the tag. */
function intlLanguageName(uiLanguage: string, language: string): string | null {
  try {
    const name = new Intl.DisplayNames([uiLanguage], { type: "language" }).of(language);
    // Intl echoes the input tag back when it has no display name for it.
    return name && name.toLowerCase() !== language.toLowerCase() ? name : null;
  } catch {
    return null;
  }
}

/**
 * The display name of a language in the current UI language, from the platform's
 * CLDR data via Intl.DisplayNames (so every tag, including draft preview
 * languages, is named automatically). Falls back to the raw tag only when the
 * platform has no name for it.
 */
const languageName = (uiLanguage: string, language: string) => intlLanguageName(uiLanguage, language) ?? language;

/**
 * The signed-in account's personal UI language (LOC-402). Reads and writes only
 * through the existing preferences controller: a value is sent only when the
 * user picks one, and "saved" is shown only after the server confirmed it.
 */
export function LanguagePreferenceCard() {
  const { t, languages, language: uiLanguage } = useLocalization(localization);
  const { status, stored, saveState, save, reload } = usePreferences(languagePreferences);
  const preview = usePreview(languagePreview);
  const id = useId();
  const triggerId = `${id}-language`;
  const descriptionId = `${id}-description`;

  const saving = saveState.status === "saving";
  const saveFailed = saveState.status === "error" && status !== "account_changed";
  // The saved language is a product choice: it is not offered while a preview is shown or requested.
  const previewing = preview.active || preview.pending !== null;
  const ready = status === "ready" && stored !== null && !previewing;
  // While a request runs it shows the requested value; otherwise the server-confirmed one.
  const shown = saving && saveState.requested !== undefined ? saveState.requested : (stored?.uiLanguage ?? null);
  const explicit = stored?.uiLanguage ?? null;
  const options = explicit !== null && !languages.includes(explicit) ? [...languages, explicit] : languages;

  const nameOf = (language: string) => {
    const name = languageName(uiLanguage, language);
    return languages.includes(language) ? name : t("common.language.unavailable", { language: name });
  };

  let body: ReactNode;
  if (status === "unknown" || (status === "loading" && stored === null)) {
    body = (
      <div role="status" className="flex flex-col gap-2">
        <Skeleton className="h-9 w-full sm:w-64" />
        <span className="sr-only">{t("common.state.loading")}</span>
      </div>
    );
  } else if (status === "error" && stored === null) {
    body = (
      <div className="flex flex-col items-start gap-3">
        <FieldError>{t("operator.account.language_load_failed")}</FieldError>
        <Button variant="outline" size="sm" onClick={() => void reload()}>
          {t("common.action.retry")}
        </Button>
      </div>
    );
  } else if (status === "signed_out") {
    body = <p className="text-sm text-muted-foreground">{t("operator.account.language_signed_out")}</p>;
  } else if (status === "account_changed" && stored === null) {
    body = <p className="text-sm text-muted-foreground">{t("operator.account.language_account_changed")}</p>;
  } else {
    // Stays enabled while saving so keyboard focus is kept; the controller lets a later choice supersede a running save.
    const choose = (value: string) => {
      const next = value === AUTOMATIC ? null : value;
      if (next !== shown) void save(next);
    };
    body = (
      <FieldGroup>
        <Field data-disabled={!ready || undefined} data-invalid={saveFailed || undefined}>
          <FieldLabel htmlFor={triggerId}>{t("common.language.label")}</FieldLabel>
          <Select value={shown ?? AUTOMATIC} onValueChange={choose} disabled={!ready}>
            <SelectTrigger
              id={triggerId}
              className="w-full sm:w-64"
              aria-describedby={descriptionId}
              aria-invalid={saveFailed || undefined}
              aria-busy={saving || undefined}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={AUTOMATIC}>{t("common.language.automatic")}</SelectItem>
                {options.map((language) => (
                  <SelectItem key={language} value={language} disabled={!languages.includes(language)}>
                    {nameOf(language)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FieldDescription id={descriptionId}>{t(previewing ? "common.language.preview_profile_locked" : "common.language.automatic_description")}</FieldDescription>
          {status === "account_changed" && <FieldDescription>{t("operator.account.language_account_changed")}</FieldDescription>}
          {saveFailed && (
            <div className="flex flex-col items-start gap-3">
              <FieldError>{t("operator.account.language_save_failed")}</FieldError>
              <Button variant="outline" size="sm" onClick={() => void save(saveState.requested ?? null)}>
                {t("common.action.retry")}
              </Button>
            </div>
          )}
        </Field>
        <PreviewControls preview={preview} t={t} uiLanguage={uiLanguage} />
      </FieldGroup>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("operator.account.language_title")}</CardTitle>
        <CardDescription>{t("operator.account.language_description")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {body}
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
          {saving ? t("operator.account.language_saving") : saveState.status === "saved" ? t("operator.account.language_saved") : null}
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Tester-only translation preview (LOC-P07): hidden unless the server admitted
 * this account. Shown in this tab only; it never changes the saved language.
 */
function PreviewControls({ preview, t, uiLanguage }: { preview: Preview; t: Translate; uiLanguage: string }) {
  const id = useId();
  const switchId = `${id}-preview`;
  const descriptionId = `${id}-preview-description`;
  const languageId = `${id}-preview-language`;
  const admitted = preview.access === "admitted";
  const error = preview.error !== null && <FieldError>{t(PREVIEW_ERROR_KEYS[preview.error])}</FieldError>;
  // Refused, ended or not yet known: no controls; an ended preview says so.
  if (!admitted && preview.access !== "unavailable" && !preview.active) return error || null;

  const on = preview.pending === "exit" ? false : preview.active || preview.pending === "enter";
  const toggle = (checked: boolean) => void (checked ? preview.enter() : preview.exit());
  return (
    <>
      <Field orientation="horizontal" data-disabled={(!admitted && !preview.active) || undefined}>
        <Switch
          id={switchId}
          checked={on}
          onCheckedChange={toggle}
          disabled={!admitted && !preview.active}
          aria-describedby={descriptionId}
          aria-busy={preview.pending !== null || undefined}
        />
        <FieldLabel htmlFor={switchId}>{t("common.language.preview_toggle")}</FieldLabel>
      </Field>
      <FieldDescription id={descriptionId}>{t("common.language.preview_description")}</FieldDescription>
      {preview.active && (
        <>
          <p role="status" className="rounded-lg bg-amber-400/15 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
            {t("common.language.preview_active")}
          </p>
          <Field data-disabled={!admitted || undefined}>
            <FieldLabel htmlFor={languageId}>{t("common.language.preview_language")}</FieldLabel>
            <Select value={preview.language ?? undefined} onValueChange={(language) => void preview.enter(language)} disabled={!admitted}>
              <SelectTrigger id={languageId} className="w-full sm:w-64" aria-busy={preview.pending === "switch" || undefined}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {preview.languages.map((language) => (
                    <SelectItem key={language} value={language}>
                      {languageName(uiLanguage, language)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
        </>
      )}
      {error}
      {preview.access === "unavailable" && (
        <div className="flex flex-col items-start gap-3">
          {preview.error === null && <FieldError>{t("common.language.preview_unavailable")}</FieldError>}
          <Button variant="outline" size="sm" onClick={() => void preview.discover()}>
            {t("common.action.retry")}
          </Button>
        </div>
      )}
    </>
  );
}
